import { dataViewService } from "../services/dataViewService";
import {
  downloadSingleDataViewRecord,
  downloadVisibleDataViewRows,
} from "../utils/downloadDataViewFiles";

export const useDataViewActions = ({
  activeDataset,
  activeConfig,
  detailSourceRecord,
  selectedRecordId,
  mode,
  pagination,
  isAdminMode,
  applyRecordUpdate,
  setDownloadError,
  setIsBulkDownloading,
  setIsSavingRecord,
  setSaveRecordError,
  setIsRevertingRecord,
  setRevertRecordError,
  setCompanyBulkError,
  setIsMergingCompanies,
  setIsDeletingCompanies,
  refreshData,
  setSelectedRecordId,
  setIsDetailPanelOpen,
  setSelectedRowIds,
}) => {
  const handleBulkDownload = async ({
    records,
    selectedRows,
    batchOptions,
    selectedBatchValue,
    isSearchMode,
  }) => {
    setDownloadError("");

    if (!records.length && isSearchMode) {
      return;
    }

    if (selectedRows.length) {
      downloadVisibleDataViewRows({
        rows: selectedRows,
        datasetName: activeConfig.label,
        fileLabel: `${activeConfig.label} selected rows`,
        currentPage: pagination.page,
      });
      return;
    }

    if (isSearchMode) {
      downloadVisibleDataViewRows({
        rows: records,
        datasetName: `${activeConfig.label} search results`,
        currentPage: pagination.page,
      });
      return;
    }

    const selectedBatch = batchOptions.find(
      (option) => option.value === selectedBatchValue
    );
    if (!selectedBatch) {
      setDownloadError("No dataset batch is available to download yet.");
      return;
    }

    setIsBulkDownloading(true);

    try {
      await dataViewService.downloadBatch(
        {
          dataset: activeDataset,
          start: selectedBatch.start,
          end: selectedBatch.end,
        },
        mode
      );
    } catch (error) {
      setDownloadError(
        error?.message || "Unable to download the selected batch right now."
      );
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const handleSingleDownload = () => {
    if (!detailSourceRecord) {
      return;
    }

    downloadSingleDataViewRecord({
      record: detailSourceRecord,
      datasetName: activeConfig.label,
      recordId: detailSourceRecord._record_id || selectedRecordId || "record",
    });
  };

  const handleSaveRecord = async (nextRecord) => {
    if (!isAdminMode || !selectedRecordId) {
      return;
    }

    setIsSavingRecord(true);
    setSaveRecordError("");

    try {
      const response = await dataViewService.updateRecordDetail(
        {
          dataset: activeDataset,
          record_id: selectedRecordId,
          record: nextRecord,
        },
        mode
      );

      const payload = response?.data || {};
      if (payload.record) {
        applyRecordUpdate(
          payload.record,
          payload.editable_record || nextRecord,
          payload.audit_metadata || null
        );
      }
    } catch (error) {
      const nextError = error?.message || "Unable to save this record right now.";
      setSaveRecordError(nextError);
      throw new Error(nextError);
    } finally {
      setIsSavingRecord(false);
    }
  };

  const handleRevertRecord = async () => {
    if (!isAdminMode || !selectedRecordId) {
      return;
    }

    setIsRevertingRecord(true);
    setRevertRecordError("");

    try {
      const response = await dataViewService.revertRecordDetail(
        {
          dataset: activeDataset,
          record_id: selectedRecordId,
        },
        mode
      );

      const payload = response?.data || {};
      if (payload.record) {
        applyRecordUpdate(
          payload.record,
          payload.editable_record || payload.record,
          payload.audit_metadata || null
        );
      }
    } catch (error) {
      const nextError = error?.message || "Unable to revert this record right now.";
      setRevertRecordError(nextError);
      throw new Error(nextError);
    } finally {
      setIsRevertingRecord(false);
    }
  };

  const handleMergeCompanies = async ({ selectedRows, primaryNameMode, primaryName }) => {
    if (!isAdminMode || activeDataset !== "company" || selectedRows.length < 2) {
      return;
    }

    setCompanyBulkError("");
    setIsMergingCompanies(true);
    try {
      const response = await dataViewService.mergeCompanies(
        {
          dataset: "company",
          record_ids: selectedRows.map((row) => row._record_id),
          primary_name_mode: primaryNameMode,
          primary_name: primaryName,
        },
        mode
      );
      const payload = response?.data || {};
      const mergedRecord = payload?.data?.record;
      const mergedRecordId = payload?.data?.record_id || mergedRecord?._record_id || null;
      setSelectedRowIds([]);
      refreshData();
      if (mergedRecordId) {
        setSelectedRecordId(mergedRecordId);
        setIsDetailPanelOpen(true);
      }
    } catch (error) {
      setCompanyBulkError(error?.message || "Unable to merge the selected companies.");
    } finally {
      setIsMergingCompanies(false);
    }
  };

  const handleDeleteCompanies = async ({ selectedRows }) => {
    if (!isAdminMode || activeDataset !== "company" || selectedRows.length < 1) {
      return;
    }
    setCompanyBulkError("");
    setIsDeletingCompanies(true);
    try {
      await dataViewService.deleteCompanies(
        {
          dataset: "company",
          record_ids: selectedRows.map((row) => row._record_id),
        },
        mode
      );
      setSelectedRowIds([]);
      refreshData();
      setSelectedRecordId(null);
    } catch (error) {
      setCompanyBulkError(error?.message || "Unable to delete the selected companies.");
    } finally {
      setIsDeletingCompanies(false);
    }
  };

  const handleSearchCompanies = async (query) => {
    const searchQuery = String(query || "").trim();
    if (!searchQuery) {
      return [];
    }

    const response = await dataViewService.searchRecords(
      {
        dataset: "company",
        query: searchQuery,
        page: 1,
        per_page: 8,
      },
      mode
    );

    return response?.data?.records || [];
  };

  const handleAddCompanySources = async ({
    selectedRows = [],
    companyRecord = null,
    companyRecordId = null,
    dataset = "",
    sourceIds = [],
  }) => {
    const manualSourceIds = Array.isArray(sourceIds)
      ? sourceIds.map((value) => String(value || "").trim()).filter(Boolean)
      : [];
    const isManualSourceFlow = manualSourceIds.length > 0;

    if (!isAdminMode) {
      return;
    }

    if (isManualSourceFlow) {
      if (!dataset || !companyRecordId) {
        return;
      }
    } else if (
      activeDataset === "company" ||
      selectedRows.length < 1 ||
      !companyRecord
    ) {
      return;
    }

    setCompanyBulkError("");
    setIsMergingCompanies(true);
    try {
      const response = await dataViewService.addCompanySources(
        isManualSourceFlow
          ? {
              dataset,
              company_record_id: companyRecordId,
              source_ids: manualSourceIds,
            }
          : {
              dataset: activeDataset,
              record_ids: selectedRows.map((row) => row._record_id),
              company_record_id: companyRecord._record_id,
            },
        mode
      );

      const payload = response?.data || {};
      const refreshedDetail = payload?.data;
      if (refreshedDetail?.record && refreshedDetail?.editable_record) {
        setSelectedRowIds([]);
        applyRecordUpdate(
          refreshedDetail.record,
          refreshedDetail.editable_record || refreshedDetail.record,
          refreshedDetail.audit_metadata || null
        );
      } else {
        setSelectedRowIds([]);
        refreshData();
      }
    } catch (error) {
      setCompanyBulkError(error?.message || "Unable to attach the selected documents to the company.");
      throw error;
    } finally {
      setIsMergingCompanies(false);
    }
  };

  const handleRemoveCompanySources = async ({
    companyRecordId = null,
    dataset = "",
    sourceIds = [],
  }) => {
    const manualSourceIds = Array.isArray(sourceIds)
      ? sourceIds.map((value) => String(value || "").trim()).filter(Boolean)
      : [];

    if (!isAdminMode || !dataset || !companyRecordId || !manualSourceIds.length) {
      return;
    }

    setCompanyBulkError("");
    setIsMergingCompanies(true);
    try {
      const response = await dataViewService.removeCompanySources(
        {
          dataset,
          company_record_id: companyRecordId,
          source_ids: manualSourceIds,
        },
        mode
      );

      const payload = response?.data || {};
      const refreshedDetail = payload?.data;
      if (refreshedDetail?.record && refreshedDetail?.editable_record) {
        applyRecordUpdate(
          refreshedDetail.record,
          refreshedDetail.editable_record || refreshedDetail.record,
          refreshedDetail.audit_metadata || null
        );
      } else {
        refreshData();
      }
    } catch (error) {
      setCompanyBulkError(error?.message || "Unable to remove the selected sources from the company.");
      throw error;
    } finally {
      setIsMergingCompanies(false);
    }
  };

  return {
    handleBulkDownload,
    handleSingleDownload,
    handleSaveRecord,
    handleRevertRecord,
    handleMergeCompanies,
    handleDeleteCompanies,
    handleSearchCompanies,
    handleAddCompanySources,
    handleRemoveCompanySources,
  };
};
